/**
 * Date utility functions for tax transaction search
 */

const HAS_TIMEZONE_RE = /([zZ]|[+-]\d{2}:?\d{2})$/

/**
 * Normalizes a date string to valid ISO 8601 with explicit UTC timezone.
 * Appends T00:00:00Z to date-only strings, Z to datetime strings missing a timezone.
 */
function normalizeDateString(date: string): string {
  if (HAS_TIMEZONE_RE.test(date)) return date
  return date + (date.includes('T') ? 'Z' : 'T00:00:00Z')
}

/**
 * Parses a date string as UTC, normalizing timezone for cross-browser compatibility.
 * Returns null if the result is invalid.
 */
export function safeParseDateUTC(date: string): Date | null {
  const d = new Date(normalizeDateString(date))
  return isNaN(d.getTime()) ? null : d
}

/**
 * Formats a date to YYYY-MM-DD format
 */
export function formatDateToISO(date: Date | null): string {
  if (!date) return ''
  return date.toISOString().split('T')[0]
}

/**
 * Parses a date string in YYYY-MM-DD format
 */
export function parseDateFromISO(dateString: string): Date | null {
  if (!dateString) return null
  const date = new Date(dateString)
  return isNaN(date.getTime()) ? null : date
}

/**
 * Validates if a date range is within the maximum allowed days
 */
export function validateDateRange(
  startDate: Date | null,
  endDate: Date | null,
  maxDays: number = 90,
): boolean {
  if (!startDate || !endDate) return true

  const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  return diffDays <= maxDays
}

/**
 * Checks if a date is within a given range (inclusive)
 */
export function isDateInRange(
  date: Date,
  startDate: Date,
  endDate: Date,
): boolean {
  return date >= startDate && date <= endDate
}

/**
 * Gets the number of days between two dates
 */
export function getDaysBetween(startDate: Date, endDate: Date): number {
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

/**
 * Formats a date for display (short format: "Dec 25, 2024")
 */
export function formatDateForDisplay(date: Date | string | null): string {
  if (!date) return ''
  // Ensure date strings without timezone are treated as UTC
  let dateObj: Date
  if (typeof date === 'string') {
    dateObj = new Date(normalizeDateString(date))
  } else {
    dateObj = date
  }
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Formats a date to 'YYYY-MM-DD' format for date-only form values and API
 * payloads (e.g. tax start dates picked from a DatePicker).
 *
 * Intentionally formats by the date's own calendar components — NOT pinned to
 * UTC. These values are date-only concepts stored as a local-midnight Date;
 * tz-converting them would shift the calendar day (off-by-one submissions for
 * users in positive UTC offsets). For timestamp display use
 * formatDateTimeForDisplay / formatDateWithTime, which are UTC.
 */
export function formatDate(date: Date | string | null): string {
  if (!date) return ''
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''
  return dateObj.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

/**
 * Formats a date with full locale string in the viewer's LOCAL timezone.
 * Use for personal/contextual timestamps (e.g. "last login") where the user
 * expects their own clock — NOT for canonical record data (use
 * formatDateTimeForDisplay for UTC).
 */
export function formatDateLocale(date: Date | string | null): string {
  if (!date) return ''
  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateObj.getTime())) return ''
  return dateObj.toLocaleString()
}

/**
 * Formats a date with time for detailed display (e.g., "Dec 25, 2024, 10:30 AM"), in UTC
 */
export function formatDateWithTime(date: Date | string | null): string {
  if (!date) return ''
  const dateObj = typeof date === 'string' ? new Date(normalizeDateString(date)) : date
  if (isNaN(dateObj.getTime())) return ''
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(dateObj)
}

/**
 * Formats a date with time in the OPERATOR'S local timezone, tagged with the
 * zone abbreviation (e.g. "Jun 4, 2026, 05:00 PM PDT").
 *
 * Use for record timestamps an operator reads against their own clock while
 * working a queue — the account onboarding list and detail pages. The zone
 * abbreviation is part of the output on purpose: without it a local-rendered
 * timestamp is indistinguishable from the UTC one formatDateWithTime produces,
 * and the two disagree by hours.
 *
 * Timezone-less input is still parsed as UTC (normalizeDateString), so a
 * backend timestamp with no offset is converted rather than reinterpreted as
 * local wall-clock time.
 */
export function formatDateWithTimeLocal(date: Date | string | null): string {
  if (!date) return ''
  const dateObj =
    typeof date === 'string' ? new Date(normalizeDateString(date)) : date
  if (isNaN(dateObj.getTime())) return ''
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(dateObj)
}

/**
 * Formats a date-time for display, including time in HH:MM (with seconds) in UTC.
 */
export function formatDateTimeForDisplay(date: Date | string | null): string {
  if (!date) return ''
  const dateObj = typeof date === 'string' ? new Date(normalizeDateString(date)) : date
  if (isNaN(dateObj.getTime())) return ''
  return dateObj.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  })
}
